import { useContext, useEffect, useRef } from 'react';
import moment from 'utils/dayjs';
import FavouriteSpace from '../components/favouriteSpace';
import Favourites from '../components/favourites';
import FavouriteVenue from '../components/favouriteVenue';
// components
import Page from '../components/page';
import PreviousSearches from '../components/previousSearches';
import QuickBook from '../components/quickBook';
import SearchResults from '../components/searchResults';
import useStoredBooking from '../hooks/useStoredBooking';
import { serverSideTranslations } from '../lib/serverSideTranslations';
// context
import { AuthContext } from '../state/auth';
import { FavouritesContext } from '../state/favourites';
import { SearchContext } from '../state/search';
import { useTranslation } from '../utils/i18n';

import { handleStickySideBar } from '../utils/stickySidebar';

const RoomsPage = () => {
  const { t } = useTranslation('common');

  const { loggedIn } = useContext(AuthContext);
  const {
    actions: { fetchFavourites, removeFavourite },
    venues: favouriteVenues,
    spaces: favouriteSpaces,
  } = useContext(FavouritesContext);
  const {
    actions: { applyPreviousSearch },
    previousSearches,
    filters: { date: searchDate },
  } = useContext(SearchContext);

  useEffect(() => {
    if (loggedIn) {
      fetchFavourites('venues');
      fetchFavourites('spaces');
    }
  }, [fetchFavourites, loggedIn]);

  useEffect(() => {
    handleStickySideBar();
  }, []);

  const quickBookRef = useRef<HTMLDivElement | null>(null);

  const [
    { spaceIds: storedSpaceIds, venueId: storedVenueId },
    setStoredBooking,
  ] = useStoredBooking();

  const selectSpace = (
    space: { id: number },
    venue: { id?: number } | undefined
  ) => {
    if (storedSpaceIds.length === 0 || storedVenueId === venue?.id) {
      setStoredBooking(storedBooking => ({
        ...storedBooking,
        venueId: venue?.id,
        spaceIds: [...storedSpaceIds, space.id],
        // The search filter stores the date as a string; normalise to the
        // Date the stored booking declares.
        date: searchDate
          ? moment(searchDate).startOf('day').toDate()
          : storedBooking.date || new Date(),
      }));
    }

    quickBookRef.current?.scrollIntoView({ behavior: 'smooth' });
    // adding as .scrollIntoView above doesn't scroll enough due to sticky header
    window.scrollBy(0, -120);
  };

  return (
    <Page title="Find a room">
      <main>
        <section>
          <div className="section-content">
            <div className="layout layout--two-col-stacked-left">
              <div className="layout__first">
                <SearchResults
                  selectedVenueId={storedVenueId}
                  selectedSpaceIds={storedSpaceIds}
                  selectSpace={selectSpace}
                />
              </div>
              <div className="layout__last">
                <div className="layout__last--sticky" id="sticky_sidebar">
                  <QuickBook ref={quickBookRef} />

                  {loggedIn && (
                    <Favourites
                      title={t('favourite.venues.title')}
                      placeholder={t('favourite.venues.placeholder')}
                      showFavourites={!!favouriteVenues.length}
                    >
                      {favouriteVenues.map(venue => (
                        <FavouriteVenue
                          key={venue.id}
                          venue={venue}
                          onRemove={() => removeFavourite(venue.id, 'venues')}
                        />
                      ))}
                    </Favourites>
                  )}

                  {loggedIn && (
                    <Favourites
                      title={t('favourite.spaces.title')}
                      placeholder={t('favourite.spaces.placeholder')}
                      showFavourites={!!favouriteSpaces.length}
                    >
                      {favouriteSpaces.map(space => (
                        <FavouriteSpace
                          key={space.id}
                          space={space}
                          onRemove={() => removeFavourite(space.id, 'spaces')}
                          selectSpace={() => selectSpace(space, space.venue)}
                          navigateToSpace
                        />
                      ))}
                    </Favourites>
                  )}

                  <PreviousSearches
                    previousSearches={previousSearches || []}
                    onClick={applyPreviousSearch}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Page>
  );
};

export const getServerSideProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

export default RoomsPage;
